import Bugsnag from "@bugsnag/js";
import classNames from "classnames";
import { FunctionComponent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import EstimatedDelivery from "@Components/atoms/EstimatedDelivery";
import BottomSheet from "@Components/molecules/BottomSheet";
import ChoicesPacks from "@Components/molecules/choices/ChoicesPacks";
import Panel from "@Components/molecules/Panel";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import { ReactComponent as IconArrowRight } from "@Icons/16x16/arrow-right.svg";
import { Section, Tile } from "@Types/pagify";
import { isMobile } from "@Utils/device";
import { getLink } from "@Utils/pagify";
import "./style.scss";

type Items = "product" | "price" | "delivery";

export const ProductDetails: FunctionComponent<{
  section: Section;
  tiles: Tile[];
}> = ({ section, tiles }) => {
  const intl = useIntl();
  const localeSlug = intl.locale;

  const [selectedItem, setSelectedItem] = useState<Items>("product");
  const [displayPacks, setDiplayPacks] = useState<boolean>(false);

  const { isLargeScreen } = useBreakpoint();

  const tile = tiles[0];
  const link = getLink(tile.navigationLink, localeSlug);

  const productKind = tile.metadata?.kind;
  const navigationItems: Items[] =
    productKind === "product_only" ? ["product", "delivery"] : ["product", "price", "delivery"];

  const priceInfos = tile.metadata?.web?.products;

  const deliveryDatesRange = tile.metadata?.deliveryDaysRange;
  const date1 = new Date();
  const date2 = new Date();
  if (deliveryDatesRange) {
    date1.setDate(date1.getDate() + deliveryDatesRange[0]);
    date2.setDate(date2.getDate() + deliveryDatesRange[1]);
  }

  const ctaText = tile.metadata?.web?.ctaText;
  const productDescription = tile.metadata?.web?.productDescription;

  const handleClick = (tile: Tile) => {
    if (isMobile()) {
      location.href += "/download-app";
    } else {
      if (
        tile.action?.entries &&
        tile.action.entries.length > 0 &&
        productKind === "product_only_with_packs"
      ) {
        setDiplayPacks(true);
      } else {
        setDiplayPacks(false);
        if (!link) {
          Bugsnag.notify(
            new Error(`navigationLink for tile ${tile.slug} not found in ProductDetails`)
          );
          return;
        }
        location.href = link;
      }
    }
  };

  const handleDismiss = () => {
    setDiplayPacks(false);
    document.body.classList.toggle("noScroll", false);
  };

  return (
    <div className="ProductDetails">
      <div className="ProductDetails-navigation">
        {navigationItems.map((item) => (
          <div
            className={classNames("ProductDetails-navigation-item", {
              "ProductDetails-navigation-item--selected": selectedItem === item,
            })}
            key={item}
            onClick={() => setSelectedItem(item)}
          >
            <FormattedMessage id={`cheerz_ProductDetails_${item}`} />
          </div>
        ))}
      </div>
      <div className="ProductDetails-content">
        {selectedItem === "product" && productDescription && (
          <div
            className="ProductDetails-product"
            dangerouslySetInnerHTML={{ __html: productDescription }}
          />
        )}
        {selectedItem === "price" && (
          <div className="ProductDetails-price">
            <table>
              {productKind !== "product_with_choices" && (
                <thead>
                  <tr>
                    <th className="ProductDetails-price-productTitle"></th>
                    <th className="ProductDetails-price-productTitle">
                      {intl.formatMessage({ id: "cheerz_ProductDetails_quantity" })}
                    </th>
                    <th className="ProductDetails-price-productTitle">
                      {intl.formatMessage({ id: "cheerz_ProductDetails_price_unit" })}
                    </th>
                  </tr>
                </thead>
              )}

              <tbody>
                {priceInfos?.map((product) => {
                  if (product.withQuantity === false) {
                    return (
                      <tr key={product.productName}>
                        {productKind === "product_with_choices" ? (
                          <td
                            className="ProductDetails-price-productName"
                            key={product.productName}
                          >
                            {product.productName}
                          </td>
                        ) : (
                          <>
                            <td className="ProductDetails-price-productName">{section.title}</td>
                            <td
                              className="ProductDetails-price-productQuantity"
                              key={product.productName}
                            >
                              {product.productName}
                            </td>
                          </>
                        )}

                        <td className="ProductDetails-price-productPrice">
                          {product.productPrice}
                        </td>
                      </tr>
                    );
                  } else if (product.withQuantity === true && product.products) {
                    return product.products.map((pack, index) => (
                      <tr
                        className={
                          index + 1 === product.products?.length ? "ProductDetails-price--last" : ""
                        }
                        key={pack.productName}
                      >
                        <td className="ProductDetails-price-productName">{product.productName}</td>
                        <td className="ProductDetails-price-productQuantity">{pack.productName}</td>
                        <td className="ProductDetails-price-productPrice">{pack.productPrice}</td>
                      </tr>
                    ));
                  }
                })}
              </tbody>
            </table>
          </div>
        )}
        {selectedItem === "delivery" && (
          <div className="ProductDetails-delivery">
            <EstimatedDelivery standardDeliveryDates={[date1.toString(), date2.toString()]} />
          </div>
        )}
      </div>
      <ButtonSmily
        icon={<IconArrowRight />}
        iconPosition="right"
        onClick={() => handleClick(tile)}
        size="m"
        type="primary"
      >
        {ctaText}
      </ButtonSmily>

      {isLargeScreen ? (
        <Panel display={displayPacks} onDismiss={handleDismiss}>
          <ChoicesPacks tile={tile} />
        </Panel>
      ) : (
        <BottomSheet display={displayPacks} onDismiss={handleDismiss} showCloseButton={true}>
          <ChoicesPacks tile={tile} />
        </BottomSheet>
      )}
    </div>
  );
};
